import { useMemo, useState, useCallback, useEffect } from "react";

import { Dialog, useNotificationContext } from "cerulean";
import { modules, entities } from "byzantine";
import Filters from "byzantine/src/filters";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import MerchantIcon from "../MerchantIcon/MerchantIcon";
import { useCards } from "../../contexts/CardsContext";
import Section from "./Section";
import Detail from "./Detail";
import CancelSubscriptionCard from "./CancelSubscriptionCard";

import styles from "./MerchantDetails.module.scss";
import ContactInfo from "./ContactInfo";

export interface MerchantDetailsProps {
  cardId: API.CardId;
  subscriptionUuid: string;
}

const MerchantDetails = ({
  cardId,
  subscriptionUuid,
}: MerchantDetailsProps) => {
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();
  const subscription = entities.subscriptions.useSubscriptionByUuid(
    cardId,
    subscriptionUuid
  );
  const { cards } = useCards();
  const card = useMemo(
    () => cards.find((c) => c.id === cardId),
    [cards, cardId]
  );

  useEffect(() => {
    if (!subscription) {
      sendNotification({
        type: "negative",
        text: "Unable to find that subscription.",
      });
      navigate(`/cards/${cardId}/subscriptions`, { replace: true });
    }
  }, [navigate, subscription, cardId]);

  const { merchant_name: merchantName } = subscription || { merchant_name: "" };
  const { paymentHistory } = modules.subscriptions.useMerchantPaymentHistory(
    cardId,
    merchantName
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleOpenCancelSubscriptionDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);
  const handleCloseCancelSubscriptionDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logoWrapper}>
          {/* TODO: Replace with real merchant logo */}
          {/* https://linear.app/narmi/issue/CDB-1044/integrate-the-api-subscriptions-and-merchant-info-removing-the-mock */}
          <MerchantIcon name={merchantName} />
        </div>
        <h2>{merchantName}</h2>
      </div>
      <Section title="MERCHANT DETAILS">
        {/* TODO: Replace with real merchant info */}
        {/* https://linear.app/narmi/issue/CDB-1044/integrate-the-api-subscriptions-and-merchant-info-removing-the-mock */}
        <Detail label="Website" value="www.netflix.com" />
        <Detail label="Card" value={card?.name || ""} />
        <Detail
          label="Estimated next payment"
          value={format(new Date(), "MMMM d, yyyy")}
        />
      </Section>
      <Section title="HISTORY">
        <div className={styles.historyList}>
          {paymentHistory.map((tx) => (
            <Detail
              key={tx.id}
              label={format(tx.transaction_date, "MMMM d, yyyy")}
              value={Filters.currency(Filters.dollarsToPennies(tx.amount))}
            />
          ))}
        </div>
      </Section>
      <CancelSubscriptionCard
        subscriptionId={subscriptionUuid}
        openCancelSubscriptionDialog={handleOpenCancelSubscriptionDialog}
      />
      <Dialog
        isOpen={isDialogOpen}
        onUserDismiss={handleCloseCancelSubscriptionDialog}
        title="Need to cancel your subscription?"
      >
        <div className={styles.dialogContent}>
          By clicking these links you will be redirected to the merchant's site.
          This information is provided as a convenience. FI has no partnership
          with this merchant.
        </div>
        {/* TODO: Replace with real merchant cancellation info */}
        {/* https://linear.app/narmi/issue/CDB-1044/integrate-the-api-subscriptions-and-merchant-info-removing-the-mock */}
        <ContactInfo
          name="Phone"
          linkLabel="1-800-123-4567"
          link="tel:1-800-123-4567"
        />
        <ContactInfo
          name="Email"
          linkLabel={
            <>
              info@max.com <span className="narmi-icon-chevron-right" />
            </>
          }
          link="mailto:info@max.com"
        />
        <ContactInfo
          name="Website"
          link="https://www.max.com/cancel"
          linkLabel={
            <>
              www.max.com/cancel <span className="narmi-icon-chevron-right" />
            </>
          }
        />
      </Dialog>
    </div>
  );
};

export default MerchantDetails;
